import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from "@exhousing/design-system";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  useAddRoom,
  usePropertyLookups,
  useRemoveRoom,
} from "@/hooks/api/useProperties";
import type { components } from "@/types/api";

type Room =
  components["schemas"]["app__domains__property_management__schemas__RoomResponse"];

export function RoomsCard({
  propertyId,
  rooms,
}: {
  propertyId: string;
  rooms: Room[];
}) {
  const { data: lookups } = usePropertyLookups();
  const add = useAddRoom(propertyId);
  const remove = useRemoveRoom(propertyId);
  const [label, setLabel] = useState("");
  const [typeId, setTypeId] = useState("");
  const [surface, setSurface] = useState("");

  const roomTypes = (lookups ?? []).filter((l) => l.category === "room_type");

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    add.mutate(
      {
        label: label.trim(),
        room_type_id: typeId || null,
        surface_m2: surface ? Number(surface) : null,
        sort_order: rooms.length,
      },
      {
        onSuccess: () => {
          setLabel("");
          setTypeId("");
          setSurface("");
        },
        onError: () => toast.error("Could not add room"),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rooms</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {rooms.length > 0 ? (
          <ul className="space-y-2">
            {rooms.map((r) => (
              <li
                key={r.id}
                className="group flex items-center justify-between border-b border-border/60 pb-2 text-sm last:border-0"
              >
                <span>{r.label}</span>
                <div className="flex items-center gap-3">
                  <span className="tabular-nums text-muted-foreground">
                    {r.surface_m2 != null ? `${r.surface_m2} m²` : "—"}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      remove.mutate(r.id, {
                        onError: () => toast.error("Could not remove room"),
                      })
                    }
                    aria-label="Remove room"
                    className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No rooms recorded.</p>
        )}

        <form onSubmit={onAdd} className="flex flex-wrap items-end gap-2">
          <Input
            placeholder="Room label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="h-10 w-40"
          />
          <select
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            className="h-10 rounded-md border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Type…</option>
            {roomTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label_en}
              </option>
            ))}
          </select>
          <Input
            type="number"
            placeholder="m²"
            value={surface}
            onChange={(e) => setSurface(e.target.value)}
            className="h-10 w-24"
          />
          <Button type="submit" size="sm" variant="outline" disabled={add.isPending}>
            Add room
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
